import { type FC, useCallback, useMemo } from 'react';

import { message } from '@vakantiesnl/components/src/utils/message';
import { mapAirportName } from '@vakantiesnl/services/src/contentful/mapper';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useGetDaysText, useGetNightsText } from '@vakantiesnl/services/src/hooks/utils';
import { useApplicationStore, useGlobalFiltersStore } from '@vakantiesnl/services/src/stores';
import { type FilterState } from '@vakantiesnl/services/src/stores/filtersStore';
import { isAccoOnlyOffer, isPackageOffer } from '@vakantiesnl/services/src/util';
import { type GTM, type Search } from '@vakantiesnl/types/src';
import { type FlashDealItem } from '@vakantiesnl/types/src/flash-deals';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';

import { ProductCard, type ProductCardProps } from './ProductCard';
import { useGetProductCardLink } from './useGetProductCardLink';

const lastYear: number = dayjs().subtract(1, 'year').toDate().getFullYear();

type ProductCardWrapperProps = {
	/** Accommodation item */
	accommodation: Search.CompactAccommodationItem | Search.FullAccommodationItem;
	/** Size of the card to support multiple variants across the website */
	size?: ProductCardProps['size'];
	/** Bool to add query params for the acco url to a specific offer */
	needQueryParams?: boolean;
	/** Flash deal item to pass */
	flashDeal?: FlashDealItem;
	/** Place in the list */
	position: number;
	/** Page type the AccoCard is displayed on */
	pageType?: GTM.GTMPageType;
	/** Filters to pass for adding filters to the url builder  */
	filters?: FilterState;
	/** Event to trigger on clicking of the product card */
	onCardClick?: () => void;
	/** Hide the href to only enable clicking or disable clicking and linking */
	hideHref?: boolean;
	/** Href to override the default href to the accommodation, used i.g. for the myclient cards */
	customHref?: string;
	/** Offer item */
	offerItem?: Search.OfferItem | Search.AccommodationOnlyOffer | null;
	/** Custom CTA button text to override the default one */
	ctaButtonText?: string;
	/** To open the link in the same tab */
	openInSameTab?: boolean;
	/** Tag to show above the content, used for myclient only */
	tag?: JSX.Element;
	/** Boolean to not show the acco rating */
	hideRating?: boolean;
	/** Additional function to call on favorite click */
	onFavoriteClick?: () => void;
	/** Additionally override globally set durations, used for inspiration pages and deals */
	needsDurationOverride?: boolean;
};

export const ProductCardWrapper: FC<ProductCardWrapperProps> = ({
	accommodation,
	pageType,
	size,
	needQueryParams = true,
	position,
	flashDeal,
	filters,
	onCardClick,
	hideHref,
	offerItem,
	ctaButtonText,
	customHref,
	openInSameTab,
	tag,
	hideRating,
	onFavoriteClick,
	needsDurationOverride,
}) => {
	const globalFilters = useGlobalFiltersStore((s) => s.filters);
	const isBot = useApplicationStore((s) => s.isBot);
	const favorites = useApplicationStore((s) => s.favorites);
	const setFavorites = useApplicationStore((s) => s.setFavorites);
	const { query, asPath } = useRouter();

	/** Accommodation offer can be passed as a prop separately  */
	const offer = offerItem ?? accommodation.offers[0];

	const microCopies = useMicroCopyContext();

	const daysText = useGetDaysText(offer?.durationDays, false, microCopies);
	const nightsText = useGetNightsText(offer?.durationNights, microCopies);
	const departureDateText = useMemo(
		() =>
			offer &&
			`${dayjs(offer.departureDateYYYYMMDD, 'YYYY-MM-DD').format('dd D MMM YYYY')} (${daysText}${nightsText ? `, ${nightsText}` : ''})`,
		[offer, daysText, nightsText],
	);

	/** Only show awards for last year */
	const accoLastYearAward = useMemo(() => accommodation.awards?.[lastYear], [accommodation.awards]);

	const accommodationPageHref = useGetProductCardLink({
		accommodation,
		offer,
		filters,
		globalFilters,
		isFlashDeal: !!flashDeal,
		isBot,
		needQueryParams,
		currentPath: asPath,
		needsDurationOverride,
	});

	/** Returns entityLevel based on the query params */
	const entityLevel = useMemo(() => {
		if (query.city) {
			return 'destination';
		} else if (query.region) {
			return 'region';
		} else if (query.country) {
			return 'country';
		}

		return 'destination';
	}, [query.city, query.country, query.region]);

	const trackAccoClick = useCallback(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/search').then(({ onAccoClick }) =>
				track(
					onAccoClick(
						position,
						accommodation,
						{
							entity_level: entityLevel,
							entity_id: '',
						},
						!!flashDeal,
						accommodation.offers[0],
						pageType,
					),
				),
			),
		);
	}, [accommodation, flashDeal, entityLevel, pageType, position]);

	const trackFavoriteClick = useCallback(
		(isFavorite: boolean) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/favorite').then(({ onFavoriteClick }) =>
					track(
						onFavoriteClick({
							position,
							acco: accommodation,
							entity: {
								entity_level: entityLevel,
								entity_id: '',
							},
							action: isFavorite ? 'deselect' : 'select',
							pageType,
						}),
					),
				),
			);
		},
		[accommodation, entityLevel, pageType, position],
	);

	const offerSummaryText = useMemo(() => {
		if (isPackageOffer(offer)) {
			return message(microCopies['offerDetails.packageSummaryParagraph'], {
				airport: mapAirportName(offer.outbound.departureAirport, microCopies),
				hasTransfer: offer.rooms[0].hasTransfer,
				mealplan: microCopies[`mealplan_label.${offer.rooms[0].board.code}`],
			});
		} else if (isAccoOnlyOffer(offer)) {
			return microCopies['offerDetails.accoOnlysummaryParagraph'];
		}
	}, [microCopies, offer]);

	/** The customHref always overrides the accommodationPageHref */
	const href = useMemo(() => customHref ?? accommodationPageHref, [accommodationPageHref, customHref]);

	const handleOnFavoriteClick = useCallback(() => {
		const isAlreadyFavorite = favorites.some((item) => item.entityId === accommodation.heliosId);

		/** Track the clicking of fav/unfav */
		trackFavoriteClick(isAlreadyFavorite);

		!!onFavoriteClick && onFavoriteClick();

		let updatedFavorites;

		if (isAlreadyFavorite) {
			/** Remove favorite if the accommodation entityId is already in a favorite item */
			updatedFavorites = favorites.filter((item) => item.entityId !== accommodation.heliosId);
		} else {
			const newFavorite = {
				entityId: accommodation.heliosId,
				timeStamp: Date.now(),
			};
			updatedFavorites = [...favorites, newFavorite];
		}

		setFavorites(updatedFavorites);
	}, [accommodation.heliosId, favorites, onFavoriteClick, setFavorites, trackFavoriteClick]);

	const isFavorite = useMemo(
		() => favorites.some((item) => item.entityId === accommodation.heliosId),
		[accommodation.heliosId, favorites],
	);

	return (
		<ProductCard
			href={hideHref ? undefined : href}
			award={accoLastYearAward}
			departureDateText={departureDateText}
			image={accommodation.previewImage}
			starRating={accommodation.starRating}
			accoType={accommodation.formattedType}
			location={accommodation.location}
			offer={offer}
			size={size ?? 'large'}
			accoName={accommodation.name}
			microCopies={microCopies}
			rating={hideRating ? undefined : accommodation.rating}
			trackAccoClick={trackAccoClick}
			onCardClick={onCardClick}
			offerSummaryText={offerSummaryText}
			ctaButtonText={ctaButtonText ?? microCopies['accoCard.viewButton']}
			openInSameTab={openInSameTab}
			tag={tag}
			onFavoriteClick={handleOnFavoriteClick}
			isFavorite={isFavorite}
		/>
	);
};
